import {
    ButtonSecondary,
    Colors,
    Icon,
    ModalDialog,
    SearchBar,
    Spinner,
    Text,
} from '@amelio/ui-library';
import { useState } from 'react';

import { OverdueEmployeeList } from './OverdueEmployeeList';

import { useGetOverdueEmployees } from '@/api/thematic-meetings';
import { useAmelioTranslations } from '@/hooks';
import { useDebounce } from '@/hooks/useDebounce';

const SEARCH_DEBOUNCE_MS = 400;
// The server refuses a longer search outright.
const SEARCH_MAX_LENGTH = 200;

interface OverdueEmployeesModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Called with the employee's id when a row is activated: the page closes this dialog and
     * opens the new-meeting modal with that employee already picked, so scheduling with someone
     * flagged here never requires searching for them again. */
    onSelectEmployee: (employeeId: string) => void;
}

export const OverdueEmployeesModal = ({
    isOpen,
    onClose,
    onSelectEmployee,
}: OverdueEmployeesModalProps) => {
    const { t } = useAmelioTranslations();
    const [search, setSearch] = useState('');
    const searchQuery = useDebounce(search, SEARCH_DEBOUNCE_MS).trim();
    const { data, isPending, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
        useGetOverdueEmployees(searchQuery);

    const employees = data?.pages.flatMap((page) => page.items) ?? [];
    const totalCount = data?.pages[0]?.totalCount ?? 0;
    const remainingCount = Math.max(totalCount - employees.length, 0);

    const renderEmployees = () => {
        if (isPending) {
            return (
                <div className={'flex justify-center py-6'}>
                    <Spinner size={'md'} />
                </div>
            );
        }
        if (isError) {
            return (
                <div role={'alert'}>
                    <Text color={Colors.ERROR}>{t('thematicMeetings.overdue.loadError')}</Text>
                </div>
            );
        }
        if (employees.length === 0) {
            return (
                <Text color={Colors.BASE_500}>
                    {searchQuery
                        ? t('thematicMeetings.overdue.noMatch')
                        : t('thematicMeetings.overdue.noEmployee')}
                </Text>
            );
        }
        return (
            <>
                <OverdueEmployeeList
                    employees={employees}
                    onSelectEmployee={onSelectEmployee}
                    className={'scrollbar-glass max-h-[60vh] overflow-y-auto'}
                />

                {hasNextPage && (
                    <ButtonSecondary
                        size={'sm'}
                        onClick={() => void fetchNextPage()}
                        loading={isFetchingNextPage}
                        disabled={isFetchingNextPage}
                        className={'mx-auto mt-3'}
                        fitContent
                        dataTestId={'overdue-employees-more'}
                    >
                        {t('thematicMeetings.overdue.moreEmployees', { count: remainingCount })}
                    </ButtonSecondary>
                )}
            </>
        );
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            closeAriaLabel={t('common.close')}
            title={t('thematicMeetings.overdue.dialogTitle')}
            titleIcon={
                <Icon
                    name={'essential-danger'}
                    size={'sm'}
                    color={Colors.WARNING_800}
                    ariaHidden
                />
            }
            headingLevel={4}
            size={'sm'}
            headerExtra={
                <SearchBar
                    size={'sm'}
                    value={search}
                    onChange={(value) => setSearch(value.slice(0, SEARCH_MAX_LENGTH))}
                    placeholder={t('thematicMeetings.overdue.searchPlaceholder')}
                    ariaLabel={t('thematicMeetings.overdue.searchPlaceholder')}
                />
            }
            dataTestId={'overdue-employees-modal'}
        >
            {renderEmployees()}
        </ModalDialog>
    );
};
